// @ts-nocheck TODO: fix types for the entire file

import styled, { StyledComponent } from '@emotion/styled';
import MuiDialog from '@mui/material/Dialog';
import MuiDialogActions from '@mui/material/DialogActions';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogTitle from '@mui/material/DialogTitle';
import { DialogTitleProps } from '@mui/material/DialogTitle';
import { Box } from '@mui/system';
import { Button as ThemeButton } from 'components/Buttons/Button';
import { IconButton as ThemeIconButton } from 'components/Buttons/IconButton';
import TypographyComponent from 'components/Typography/Typography';
import { theme } from 'theme';
import pxToEm from 'theme/pxToEm';
import unitsToRem from 'theme/unitsToRem';
import { MBDialogProps } from './MBDialog.types';

interface StyledProps {
  dialogWidth?: number;
  dialogHeight?: number;
  titleBottomPadding?: number;
  paddingSize?: MBDialogProps['paddingSize'];
  showActionTopBorder?: boolean;
  isPalette?: boolean;
  dialogPaletteUnitsSize: number;
  mobileFullscreen?: boolean;
  fullScreenModal?: boolean;
}

interface DialogContentProps {
  shouldDisableBottomPadding?: boolean;
}

export const Dialog = styled(MuiDialog, {
  shouldForwardProp: (prop) =>
    ![
      'isPalette',
      'dialogWidth',
      'dialogPaletteUnitsSize',
      'paddingSize',
      'titleBottomPadding',
      'fullScreenModal',
      'isMobile',
    ].includes(String(prop)),
})<Partial<StyledProps>>`
  margin: auto;
  height: 100%;
  ${({ isPalette, dialogPaletteUnitsSize = 11 }) =>
    isPalette
      ? `
    .MuiDialog-container {
      & > div,
      & > a,
      & > button,
      & > p {
        font-size: ${unitsToRem(dialogPaletteUnitsSize)};
      }
    }
       `
      : ''}
  ${theme.breakpoints.down('md')} {
    width: 100%;
    align-items: flex-start;
    align-content: flex-start;
    margin-top: 0;
  }
  .MuiDialog-container {
    & > .MuiPaper-root {
      margin: 0;
      ${({ fullScreen }) => (fullScreen ? 'max-height: 90%;' : '')}
      ${({ fullScreen }) => (fullScreen ? 'align-self: flex-end;' : '')};
      padding: ${({ paddingSize }) =>
        paddingSize === 'medium'
          ? `${pxToEm(32)} ${pxToEm(32)} ${pxToEm(24)} ${pxToEm(32)}`
          : `${pxToEm(32)} ${pxToEm(24)} ${pxToEm(12)} ${pxToEm(24)}`};
      ${theme.breakpoints.up('md')} {
        border-radius: 20px;
        width: ${(props) => pxToEm(props.dialogWidth || 600)};
        max-width: ${(props) => pxToEm(props.dialogWidth || 600)};
        height: ${(props) => pxToEm(props.dialogHeight || 'auto')};
        ${(props) =>
          props.fullScreenModal
            ? `width: 100vw;
            max-width: 90vw;`
            : ''}
      ${theme.breakpoints.down('md')} {
        width: 100%;
        margin: 0;
        box-shadow: none;
        border-radius: ${({ fullScreen }) =>
          fullScreen ? `${pxToEm(30)} ${pxToEm(30)} 0 0` : '0'};
        padding: ${pxToEm(20)};
        height: ${(props) => pxToEm(props.dialogHeight || 'auto')};
      }
    }
  }
  .MuiDialogTitle-root {
    ${({ titleBottomPadding }) => `padding: 0 0 ${pxToEm(titleBottomPadding)}`};
    font-size: 1em;
  }
  .MuiDialogContent-root {
    padding-left: 0;
    padding-right: 0;
    padding-bottom: 0;
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-track {
      background: ${theme.palette.grey[400]};
    }
    &::-webkit-scrollbar-thumb {
      background-color: ${theme.palette.grey[600]};
      border-radius: 4px;
      border: 3px none ${theme.palette.common.white};
    }
  }
`;

export const ActionBorder = styled.div<Partial<StyledProps>>`
  margin-inline: ${({ paddingSize }) =>
    paddingSize === 'medium' ? pxToEm(-32) : pxToEm(-24)};
  border-top: ${({ theme }) => `1px solid ${theme.palette.secondary?.main}`}};
`;

export const DialogActions = styled(MuiDialogActions)`
  margin-top: auto;
  display: flex;
  justify-content: flex-end;
  padding: 12px 0 0;
  & > :not(:first-of-type) {
    margin-left: 8px;
  }
`;

export const LeftButton = styled(ThemeButton)`
  margin-right: auto;
`;

export const Button = styled(ThemeButton)`
  font-size: ${pxToEm(16)};
  padding: 0.75em 1.5em;
  &.Mui-disabled {
    background-color: ${({ theme }) => theme.palette.secondary?.main};
    color: ${({ theme }) => theme.palette.primary.main};
    opacity: 0.5;
  }
`;

export const CloseIconButton = styled(ThemeIconButton)`
  position: absolute;
  right: 2em;
  top: 1.5em;
  font-size: 1em;
  padding: 0;
  box-shadow: none !important;
  color: ${({ theme }) => theme.palette.primary.main};
  ${theme.breakpoints.down('md')} {
    top: 1.15em;
    right: 1.15em;
  }
`;

export const DialogContent = styled(MuiDialogContent, {
  shouldForwardProp: (prop) =>
    !['shouldDisableBottomPadding', 'doNotScrollContent'].includes(
      String(prop),
    ),
})<DialogContentProps>`
  margin-bottom: ${(props) =>
    props?.shouldDisableBottomPadding ? 0 : pxToEm(32)};
  ${(props) => (props?.doNotScrollContent ? 'overflow: visible;' : '')}
`;

export const BackHeadline = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: 0.5em;
  align-self: flex-start;
  margin-top: -0.5em;
`;

export const GoBackButton = styled(ThemeIconButton)`
  font-size: 1.25em;
  margin-left: -8px;
  color: ${({ theme }) => theme.palette.primary.main};
  &:hover {
    background: none !important;
    box-shadow: none !important;
  }
`;

export const GoBackTitle = styled(TypographyComponent)`
  font-weight: bold;
  font-size: 20px;
`;

export const Wrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

interface DialogTitleCustomProps extends DialogTitleProps {
  isSmallerThanTablet?: boolean;
  isSmallMobileHeadline?: boolean;
}

export const DialogTitle: StyledComponent<DialogTitleCustomProps> = styled(
  MuiDialogTitle,
  {
    shouldForwardProp: (prop) =>
      !['isSmallerThanTablet', 'isSmallMobileHeadline'].includes(String(prop)),
  },
)<DialogTitleCustomProps>`
  padding: 0 !important;
  ${theme.breakpoints.down('md')} {
    padding: ${pxToEm(5)} 0 0 0;
    ${({ isSmallMobileHeadline }) =>
      isSmallMobileHeadline &&
      `
      h5 {
        font-size: 20px !important;
        font-weight: 600 !important;
      }
    `}
  }
  ${theme.breakpoints.up('md')} {
    margin-top: -0.25em;
  }
`;
